import { FC, memo, useCallback, useRef, useState } from "react";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";
import PagePlaceholder from "~/assets/page_placeholder.svg";
import { PageItem } from "~/features/chat/hooks/useDocList";
import { cn } from "~/lib/utils";
import { TruncatedSectionTitle } from "./TruncatedSectionTitle";
import { useGetUrls } from "./useGetUrls";

type DocViewerItemProps = {
  page: PageItem;
  isSelected: boolean;
  isCurrent: boolean;
  togglePageSelection: (pageId: string) => void;
  canLoad: boolean;
  isZoomActive?: boolean;
};

const DocViewerItemComponent: FC<DocViewerItemProps> = ({
  page,
  isSelected,
  isCurrent,
  togglePageSelection,
  canLoad,
  isZoomActive = false,
}) => {
  const {
    pageId,
    getUrls,
    opensSection,
    rotate,
    width: originalWidth,
    height: originalHeight,
  } = page;

  const zoomContainerRef = useRef<HTMLDivElement>(null);
  const [hasRetinaError, setHasRetinaError] = useState(false);

  const { data: pageUrlsRotate, isFetching } = useGetUrls(
    pageId,
    getUrls,
    canLoad
  );

  const rotation = pageUrlsRotate?.rotate ?? 0;

  let ratio = originalWidth / originalHeight;

  if (rotate % 180 === 0) {
    ratio = 1 / ratio;
  }
  const scale = rotation % 180 === 0 ? 1 : ratio;

  const getImageUrl = () => {
    const urls = pageUrlsRotate?.urls;
    if (!urls) return PagePlaceholder;

    const isRetinaDisplay = window.devicePixelRatio > 1;

    if (isZoomActive) {
      if (!hasRetinaError && urls.retinaUrl) {
        return urls.retinaUrl;
      }
      return urls.highResUrl || urls.scanUrl || PagePlaceholder;
    }

    if (isRetinaDisplay) {
      if (!hasRetinaError && urls.retinaUrl) {
        return urls.retinaUrl;
      }
      return urls.highResUrl || urls.scanUrl || PagePlaceholder;
    }

    return urls.highResUrl || urls.scanUrl || PagePlaceholder;
  };

  const getSrcSet = () => {
    const urls = pageUrlsRotate?.urls;
    if (!urls) return undefined;

    const srcSetParts = [`${urls.highResUrl} 1x`];

    if (!hasRetinaError && urls.retinaUrl) {
      srcSetParts.push(`${urls.retinaUrl} 2x`);
    }

    return srcSetParts.length > 0 ? srcSetParts.join(", ") : undefined;
  };

  const imageUrl = getImageUrl();
  const srcSet = getSrcSet();

  const handleImageError = useCallback(() => {
    if (!hasRetinaError) {
      setHasRetinaError(true);
    }
  }, [hasRetinaError]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!zoomContainerRef.current) return;

    const container = zoomContainerRef.current;
    const rect = container.getBoundingClientRect();
    const zoomImg = container.querySelector(".iiz__zoom-img") as HTMLElement;

    if (!zoomImg) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const normalizedX = Math.max(0, Math.min(1, x / rect.width));
    const normalizedY = Math.max(0, Math.min(1, y / rect.height));

    const imgRect = zoomImg.getBoundingClientRect();
    const maxOffsetX = Math.max(0, (imgRect.width - rect.width) / 2);
    const maxOffsetY = Math.max(0, (imgRect.height - rect.height) / 2);

    const offsetX = (normalizedX - 0.5) * maxOffsetX * 1.0;
    const offsetY = (normalizedY - 0.5) * maxOffsetY * 1.0;

    zoomImg.style.transform = `translate(${-offsetX}px, ${-offsetY}px)`;
  }, []);

  return (
    <div className="flex flex-col gap-1 justify-end w-full h-full">
      {opensSection && (
        <TruncatedSectionTitle name={opensSection.sectionName} />
      )}
      <div
        ref={zoomContainerRef}
        onClick={() => togglePageSelection(pageId)}
        onMouseMove={isZoomActive ? handleMouseMove : undefined}
        className={cn(
          "cursor-pointer flex justify-center items-center overflow-hidden rounded-sm h-full",
          isSelected && "ring-2 ring-blue-400/70",
          isCurrent &&
            "shadow-[0px_1px_2px_rgba(0,0,0,0.05),0px_1px_4px_rgba(0,0,0,0.05),0px_1px_8px_rgba(0,0,0,0.05),0px_1px_16px_rgba(0,0,0,0.05)]"
        )}
      >
        {isFetching ? (
          <div className="w-full h-full bg-muted animate-pulse" />
        ) : isZoomActive ? (
          <div className="w-full h-full overflow-hidden">
            <InnerImageZoom
              src={imageUrl}
              zoomSrc={imageUrl}
              className="w-full h-full"
              imgAttributes={{
                alt: `Page ${pageId}`,
                style: {
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                  transform: `rotate(${rotation}deg) scale(${scale})`,
                },
              }}
              zoomType="hover"
              moveType="pan"
              zoomScale={1}
            />
          </div>
        ) : (
          <img
            src={imageUrl}
            srcSet={srcSet}
            alt={`Page ${pageId}`}
            loading="lazy"
            className="object-contain transition-all duration-300"
            style={{
              transform: `rotate(${rotation}deg) scale(${scale})`,
            }}
            onError={handleImageError}
          />
        )}
      </div>
    </div>
  );
};

export const DocViewerItem = memo(DocViewerItemComponent);

DocViewerItem.displayName = "DocViewerItem";
